import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import ColorBox, { ColorBoxRef } from 'devextreme-react/color-box'
import { FormErrors } from 'components/forms/form-errors'
import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { useLabelWidth } from 'hooks/use-label-width'
import useResizeObserver from '@react-hook/resize-observer'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import './index.scss'

export const ColorInput = ({label, readOnly = false, ...props}: any) => {
    const { setFieldValue } = useFormikContext()
    const [field, meta] = useField(props)
    const colorBoxRef = useRef<ColorBoxRef>(null)
    const [inFocus, setInFocus] = useState(false)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    
    const [fieldWidth, setFieldWidth] = useState<any>(null)

    const getLabelWidth = useLabelWidth(formFieldType)

    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((colorBoxRef.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        if (isFloatingLabel) {
            const colorBox: any = colorBoxRef.current
            const floatingLabel: HTMLElement = colorBox?.instance()?.element().querySelector('.dx-label')

            if (floatingLabel) {
                const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                const floatingLabelWidth = getLabelWidth(label, fontFamily)
                const input: any = colorBox?.instance()?.element().querySelector('input.dx-texteditor-input')

                fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth-15)}px`)
            }

            if (!field.value && !colorBox?.instance()?.element().className.includes('dx-dropdowneditor-active')) {
                colorBox?.instance()?.element().classList.remove('input-has-value')
                floatingLabel && (floatingLabel.style.left = inFocus ? `calc(100% - 40px - ${floatingLabel.offsetWidth-20}px)` : '20px')
            }
            else {
                colorBox?.instance()?.element().classList.add('input-has-value')
                floatingLabel && (floatingLabel.style.left = `calc(100% - 40px - ${floatingLabel.offsetWidth-20}px)`)
            }
        }


    },[field.value, getLabelWidth, label, fieldWidth, inFocus, isFloatingLabel])

    const handleChange = (e: any) => {
        setFieldValue(field.name, e.value, true)
    }

    const handleBlur = (e: any) => {
        field.onBlur(e.event)
        setInFocus(false)
    }

    const onFocusIn = useCallback(() => {
        setInFocus(true)
    }, [])

    const ddButtons = useMemo<any>(() => ([
        { name: 'dropDown' },

        {
            location: 'before',
            name: 'label',
            options: {
                text: label,
                stylingMode: 'text',
                hoverStateEnabled: false,
                focusStateEnabled: false,
                activeStateEnabled: false,
                elementAttr: {
                    class: 'label-inside-field'
                }
            }

        }
    ]), [label])

    return (
        <div className='rb-item mb-1'>
            <div>
                <label htmlFor={props.id || field.name} className='dx-field-label'>{label}</label>
                <ColorBox
                    ref={colorBoxRef}
                    label={label}
                    labelMode= 'floating'
                    buttons={ddButtons}
                    name={field.name}
                    inputAttr={{name: field.name}}
                    value={field.value}
                    onValueChanged={handleChange}
                    onFocusIn={onFocusIn}
                    onFocusOut={handleBlur}
                    acceptCustomValue={true}
                    isValid={!(meta.touched && meta.error)}
                    disabled={readOnly}
                    height={26}
                />
            </div>
            <FormErrors meta={meta} />
        </div>
    )
}
